/**
 * データ検索で利用
 *
 * APIの実行
 * ページング処理（無限スクロール含む）
 * @module compositions/base-search
 *
 */
import { reactive, toRefs } from '@nuxtjs/composition-api';
import { requestApi } from '@/apis';
import { IPaigingData } from '@/types';
import { useStore } from '@/compositions/store';
import { appStore } from '@/store';

/**
 *
 * @interface State
 *
 * url: string; APIのパス
 * doInfiniteLoad: boolean; 無限スクロースする・しない
 * onlyOne: boolean; // 詳細表示用（１件限定）
 * ignorePageing: boolean; // ページングの条件を設定しない
 * pagingParams: { rows: number; page: number }; 検索条件
 * loaded: boolean;
 * items: any; apiのResponseのデータ情報
 * doToast: boolean; トースト表示
 * pagingData: IPaigingData; apiのResponseのページング情報
 * overlay: boolean;オーバーレイ表示
 */
interface State {
  method: string;
  url: string;
  doInfiniteLoad: boolean;
  doSaveCondition: boolean;
  onlyOne: boolean;
  ignorePageing: boolean;
  pagingParams: { rows: number | string; page: number };
  loaded: boolean;
  items: any;
  item: {};
  doToast: boolean;
  pagingData: IPaigingData;
  overlay: boolean;
}

/**
 * 検索処理
 *
 * @return {*}  {*}
 */
const Search = (): any => {
  const state = reactive<State>({
    method: 'post',
    url: '',
    doInfiniteLoad: false,
    doSaveCondition: false,
    onlyOne: false,
    ignorePageing: false,
    pagingParams: { rows: 100, page: 1 },
    loaded: false,
    items: [],
    item: {},
    doToast: true,
    pagingData: {
      total_rows: 0,
      total_pages: 0,
      page: 1,
      rows: 0,
      is_first_page: true,
      prev_page: null,
      is_last_page: false,
      next_page: null
    },
    overlay: false
  });

  const { setConditions, addToastMessage, overlay } = useStore();

  /**
   * 検索APIの設定
   *
   * @param {string} url APIのURL
   * @param {{
   *       method:string, APIのmethod
   *       onlyOne: boolean, APIのレスポンスのデータが1件ではない時にエラーを返す
   *       ignorePageing: boolean, ページングの条件を設定しない
   *       page: { rows: number | string; page: number },
   *       doInfiniteLoad: boolean, 無限スクロールをする
   *       doSaveCondition: boolean 検索条件を保持するか（APIのURLをキーに条件を保存する）
   *       getFromBam?: boolean, APIのエンドポイントがBAMか
   *       doToast?: boolean, トースト表示をするか
   *       doOverlay?: boolean, オーバーレイ
   *     }} [option]
   */
  const setSearchConfig = (
    url: string,
    option?: {
      method: string;
      onlyOne: boolean;
      ignorePageing: boolean;
      page: { rows: number | string; page: number };
      doInfiniteLoad: boolean;
      doSaveCondition: boolean;
      getFromBam: boolean;
      doToast: boolean;
      doOverlay: boolean;
    }
  ): void => {
    state.url = url;
    if (option) {
      if (option.method) {
        state.method = option.method;
      }
      if (option.onlyOne !== undefined) {
        state.onlyOne = option.onlyOne;
      }
      if (option.ignorePageing !== undefined) {
        state.ignorePageing = option.ignorePageing;
      }
      if (option.page) {
        state.pagingParams = option.page;
      }
      if (option.doInfiniteLoad !== undefined) {
        state.doInfiniteLoad = option.doInfiniteLoad;
      }
      if (option.doSaveCondition !== undefined) {
        state.doSaveCondition = option.doSaveCondition;
      }

      if (option.getFromBam) {
        state.url = appStore.appConfig.bamanager_url + state.url;
      }

      if (option.doToast !== undefined) {
        state.doToast = option.doToast;
      }

      if (option.doOverlay !== undefined) {
        state.overlay = option.doOverlay;
      }
    }
  };

  /**
   * APIの実行
   *
   * @template T
   * @param {{
   *     conditions?: T; リクエストパラメーター
   *     url?: string; APIのURL
   *   }} [paramaters]
   * @return {*}  {Promise<any>}
   */
  const loadData = async <T extends {}>(paramaters?: {
    conditions?: T;
    url?: string;
  }): Promise<any> => {
    if (paramaters && paramaters.url) {
      state.url = paramaters.url;
    }

    let params = state.ignorePageing ? {} : state.pagingParams;
    if (paramaters && paramaters.conditions) {
      params = {
        ...params,
        ...paramaters.conditions
      };
    }

    // 検索条件を保持する
    if (state.doSaveCondition) {
      setConditions(state.url, params);
    }

    let result = {};

    if (state.overlay) {
      overlay.value = true;
    }

    // API実行
    await requestApi(state.url, params, state.method)
      .then((data: any) => {
        if (data && data.status === 'success') {
          if (!data.result.list) {
            if (data.error_message) {
              throw new Error(data.result.error_message);
            }
            result = data.result;
            return result;
          }
          if (state.onlyOne) {
            if (data.result.list) {
              if (data.result.list.length !== 1) {
                if (state.doToast) {
                  addToastMessage('データが確定できませんでした', 'error');
                }
              } else {
                state.item = data.result.list[0];
                state.items = data.result.list;
              }
            }
          } else {
            if (state.ignorePageing) {
              state.items = data.result.list;
            } else if (state.items) {
              state.items.push(...data.result.list);
            } else {
              state.items = data.result.list;
            }
            if (data.result.paging_data) {
              state.pagingData = data.result.paging_data;
            }
          }
          result = data.result;
        } else if (state.doToast) {
          // 予期せぬエラー発生
          addToastMessage('データ取得に失敗しました', 'error');
        }
      })
      .catch((e: any) => {
        console.error('データ取得APIエラー', e);
        if (state.doToast) {
          addToastMessage('データ取得に失敗しました', 'error');
        }
      });
    if (state.overlay) {
      overlay.value = false;
    }
    state.loaded = true;
    // 呼び元でページング処理を使わない場合はstate.itemsではなくこのresultを使う
    return result;
  };

  /**
   * APIの実行(State保持しない)
   *
   * @template T
   * @param {string} url APIのURL
   * @param {T} conditions リクエストパラメーター
   * @param {string} method
   * @param {boolean} getFromBam APIのエンドポイントがBAMか
   * @param {boolean} getFullResponse APIのレスポンスをすべて返すか
   * @param {boolean} doOverlay 実行中doOverlayするか
   * @param {string} error: string; 失敗メッセージ
   * @param {boolean} doToast: boolean; トースト表示
   * @return {*}  {Promise<any>}
   */
  const loadDataWoState = async <T extends {}>( // NOTE: Woは`Without`であることを確認済。しかしいずれ命名を修正したい
    url: string,
    conditions?: T,
    method?: string,
    getFromBam?: boolean,
    getFullResponse?: boolean,
    doOverlay?: boolean,
    error?: string,
    doToast?: boolean
  ): Promise<any> => {
    let result = {};

    const params = conditions || {};
    const apiMethod = method || 'post';

    if (getFromBam) {
      url = appStore.appConfig.bamanager_url + url;
    }

    if (doOverlay) {
      overlay.value = true;
    }
    await requestApi(url, params, apiMethod)
      .then((data: any) => {
        // if (data && data.result && data.status === 'success') {
        if (data) {
          if (data.status && data.status === 'success') {
            if (data.result) {
              if (getFullResponse) {
                result = data;
              } else {
                result = data.result;
              }
            }
          } else {
            result = data;
          }
        }

        if (data && data.status === 'success') {
          if (data.result) {
            if (getFullResponse) {
              result = data;
            } else {
              result = data.result;
            }
          }
        }
        if (!data && doToast) {
          if (error) {
            addToastMessage(error, 'error');
          } else {
            addToastMessage('データ取得に失敗しました', 'error');
          }
        }
      })
      .catch((e: any) => {
        console.error('データ取得APIエラー', e);
        addToastMessage('データ取得に失敗しました', 'error');
      });

    if (doOverlay) {
      overlay.value = false;
    }
    return result;
  };

  /**
   * 無限スクロール
   *
   * @param {*} $state
   */
  // 現在(2021.5)モジュール依存の関係？で動かなくなってしまっています。解決に時間がかかりそうなのでいったん使用禁止でお願いします
  // const infiniteHandler = ($state: any): void => {
  //   if (!state.doInfiniteLoad || !state.ignorePageing) {
  //     $state.complete();
  //     return;
  //   }
  //   // データセットが完了していない
  //   if (!state.loaded) {
  //     $state.loaded();
  //   } else {
  //     // 次のページのデータを取得
  //     state.pagingParams.page++;
  //     if (state.pagingParams.page <= state.pagingData.total_pages) {
  //       loadData().then(() => {
  //         $state.loaded();
  //       });
  //     } else {
  //       $state.complete();
  //     }
  //   }
  // };
  /**
   * ファイルの取得
   *
   * @template T
   * @param {string} url APIのURL
   * @param {T} conditions リクエストパラメーター
   * @param {string} accept リクエストheaders:Accept
   * @return {*}  {Promise<any>}
   */
  const loadFile = async <T extends {}>(url: string, conditions?: T, accept?: string): Promise<any> => {
    const type = !accept ? accept : 'application/pdf';
    const option = {
      headers: {
        Accept: type
      },
      method: 'get',
      responseType: 'blob'
    };
    overlay.value = true;

    const params = conditions || {};
    const data = await requestApi(url, params, option.method, option);

    overlay.value = false;

    return data || null;
  };

  /**
   * 次のページのデータを取得
   * doInfiniteLoadがtrueの時はこのメソッドは使わない
   * (２回ページングがされてしますので)
   */
  const NextPage = (): void => {
    if (!state.ignorePageing) {
      state.pagingParams.page++;
      if (state.pagingParams.page <= state.pagingData.total_pages) {
        loadData();
      }
    }
  };

  const resetItems = (): void => {
    state.items = [];
  };

  return {
    setSearchConfig,
    loadData,
    // infiniteHandler,
    loadFile,
    NextPage,
    loadDataWoState,
    resetItems,
    ...toRefs(state)
  };
};

export { Search };
